/**
 * PILOT-3 (GDPR): Staff Data Processing Notice
 *
 * Shown once to staff on first login. Dismissed state stored in localStorage.
 * Staff are data processors — this notice satisfies GDPR Article 29 (processor
 * obligations) and serves as an internal awareness requirement.
 *
 * Key differences from guest consent banner:
 *  - This is an informational notice, NOT a consent gate (employment is the legal basis)
 *  - Dismissible once; does not block login
 *  - No checkbox — staff don't need to consent, they need to be informed
 */

export const STAFF_NOTICE_STORAGE_KEY = 'zaxia_staff_dpa_notice_v1';

/** Returns true if the staff member has already dismissed the notice */
export function hasStaffDismissedNotice(): boolean {
  try {
    return localStorage.getItem(STAFF_NOTICE_STORAGE_KEY) === 'dismissed';
  } catch {
    return false;
  }
}

interface StaffDataProcessingNoticeProps {
  onDismiss: () => void;
}

export function StaffDataProcessingNotice({ onDismiss }: StaffDataProcessingNoticeProps) {
  const handleDismiss = () => {
    try {
      localStorage.setItem(STAFF_NOTICE_STORAGE_KEY, 'dismissed');
    } catch {
      // localStorage unavailable — dismiss anyway
    }
    onDismiss();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Data processing notice"
    >
      <div className="w-full max-w-lg bg-[#0f1923] border border-white/10 rounded-2xl p-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-amber-400 text-sm">⚠</span>
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">Data Processing Notice</h2>
            <p className="text-gray-500 text-xs">Required under GDPR — please read before continuing</p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-gray-300 leading-relaxed mb-6">
          <p>
            As a ZAXIA staff user, you have access to guest personal data including names,
            room numbers, and service request history.
          </p>

          <p>
            By using this portal, you confirm that you will:
          </p>

          <ul className="list-disc list-inside space-y-1 text-gray-400 pl-2">
            <li>Access guest data only for legitimate operational purposes</li>
            <li>Not share, export, or copy guest data outside authorised systems</li>
            <li>Report any suspected data breach to your supervisor immediately</li>
            <li>Ensure your login session is not left unattended</li>
          </ul>

          <p>
            Guest data is automatically deleted after <strong className="text-white">7 days</strong>.
            Unauthorised access or misuse of personal data may constitute a GDPR violation
            and is subject to disciplinary action.
          </p>
        </div>

        <button
          onClick={handleDismiss}
          className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors"
        >
          I understand — Continue to Staff Portal
        </button>

        <p className="text-center text-xs text-gray-600 mt-3">
          This notice is shown once. Contact your manager with any questions.
        </p>
      </div>
    </div>
  );
}
